"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { SimpleColumn } from "./types";
import { useTablePrefs } from "./useTablePrefs";
import { getDefaultColumnOrder, mergeColumnOrder } from "./column-order";

/**
 * Custom hook for managing column preferences.
 *
 * Column visibility and order are persisted PER USER, PER TABLE in
 * `app_users.user_prefs` (via `useTablePrefs` → `GET|PATCH /api/user/preferences`),
 * NOT in per-browser localStorage. The `storageKey` is the table identifier used
 * as the per-table preferences key. Column WIDTH and PAGE SIZE are persisted
 * separately in the live SimpleTable state (`useSimpleTableState`), also via
 * `useTablePrefs` under the same key — see USER_PREFERENCES.md.
 *
 * Return signature is unchanged so `ColumnManagerProvider` and its consumers are
 * untouched. A one-time migration from the legacy localStorage blob is handled
 * inside `useTablePrefs`.
 */
export function useColumnStorage(storageKey: string, defaultColumns: SimpleColumn[]) {
  const {
    columnVisibility,
    columnOrder: prefColumnOrder,
    isHydrated,
    persistColumnVisibility,
    persistColumnOrder,
  } = useTablePrefs(storageKey);

  const [columns, setColumnsState] = useState<SimpleColumn[]>(defaultColumns);
  const [columnOrder, setColumnOrderState] = useState<string[]>(() =>
    getDefaultColumnOrder(defaultColumns)
  );

  // True once a user gesture has edited columns/order — suppresses persisting the
  // one-time hydration-apply back to the server (which would be a redundant echo).
  const userTouchedRef = useRef(false);
  // Latest server prefs, read inside the apply effect WITHOUT being an effect dep.
  // Depending on the prefs directly would loop: a user edit persists → the
  // useTablePrefs prefs state updates (new object ref) → the apply effect re-runs →
  // rewrites `columns` (new ref) → the persist effect fires → persists again → …
  const latestPrefsRef = useRef({ columnVisibility, prefColumnOrder });
  latestPrefsRef.current = { columnVisibility, prefColumnOrder };
  // Last values actually persisted, so the persist effects skip no-op writes
  // (breaks any residual echo loop regardless of ref churn).
  const lastPersistedVisibilityRef = useRef<string | null>(null);
  const lastPersistedOrderRef = useRef<string | null>(null);

  // Apply server preferences to the column list ONCE per hydration, and again
  // only when the default columns change (e.g. role-gated columns appear). Reads
  // the latest prefs via a ref so it does NOT re-run on every prefs mutation.
  useEffect(() => {
    if (!isHydrated) return;
    const { columnVisibility: vis, prefColumnOrder: ord } =
      latestPrefsRef.current;
    setColumnsState(
      defaultColumns.map((col) => ({
        ...col,
        visible: vis?.[col.id] ?? col.visible,
      }))
    );
    setColumnOrderState(mergeColumnOrder(defaultColumns, ord));
  }, [isHydrated, defaultColumns]);

  // Persist visibility after a user gesture (not the hydration-apply above), and
  // only when it actually changed.
  useEffect(() => {
    if (!isHydrated || !userTouchedRef.current) return;
    const visibility: Record<string, boolean> = {};
    for (const col of columns) visibility[col.id] = col.visible;
    const serialized = JSON.stringify(visibility);
    if (serialized === lastPersistedVisibilityRef.current) return;
    lastPersistedVisibilityRef.current = serialized;
    persistColumnVisibility(visibility);
  }, [columns, isHydrated, persistColumnVisibility]);

  // Persist order after a user gesture (not the hydration-apply above), and only
  // when it actually changed.
  useEffect(() => {
    if (!isHydrated || !userTouchedRef.current) return;
    const serialized = JSON.stringify(columnOrder);
    if (serialized === lastPersistedOrderRef.current) return;
    lastPersistedOrderRef.current = serialized;
    persistColumnOrder(columnOrder);
  }, [columnOrder, isHydrated, persistColumnOrder]);

  const setColumns = useCallback<typeof setColumnsState>((update) => {
    userTouchedRef.current = true;
    setColumnsState(update);
  }, []);

  const setColumnOrder = useCallback<typeof setColumnOrderState>((update) => {
    userTouchedRef.current = true;
    setColumnOrderState(update);
  }, []);

  return {
    columns,
    columnOrder,
    isLoading: !isHydrated,
    setColumns,
    setColumnOrder,
  };
}
